// Read + delete view of an org's datasets. Pagination is "Load more"
// (cursor-based) rather than numbered pages — backend uses opaque
// cursors and there's no total count, so page numbers would be a lie.

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDatasetsList, useDeleteDataset } from '@/hooks/useDatasets';
import { extractApiError } from '@/lib/api';
import type { Dataset } from '@/lib/datasets';
import { friendlyError } from '@/lib/error-messages';
import { Loader2, Trash2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

interface DatasetTableProps {
  emptyState?: React.ReactNode;
}

export function DatasetTable({ emptyState }: DatasetTableProps) {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = React.useState<Dataset[]>([]);
  const [pendingDelete, setPendingDelete] = React.useState<Dataset | null>(null);

  const list = useDatasetsList({ cursor, limit: 20 });
  const del = useDeleteDataset();

  // On the first page, render the query data directly — no local accumulator,
  // so invalidation (e.g. after an upload) shows the new row immediately.
  // On "Load more" pages we accumulate appended results in state.
  React.useEffect(() => {
    if (cursor === undefined || !list.data) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((d) => d.id));
      return [...prev, ...list.data.items.filter((d) => !seen.has(d.id))];
    });
  }, [list.data, cursor]);

  const items = cursor === undefined ? (list.data?.items ?? []) : accumulated;

  function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    del.mutate(target.id, {
      onSuccess: () => {
        setAccumulated((prev) => prev.filter((d) => d.id !== target.id));
        toast.success(`Deleted "${target.name}"`);
        setPendingDelete(null);
      },
      onError: (err) => {
        const apiErr = extractApiError(err);
        toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Delete failed.'));
        setPendingDelete(null);
      },
    });
  }

  if (list.isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading datasets…
      </div>
    );
  }

  if (list.isError) {
    return (
      <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load datasets — please refresh.
      </div>
    );
  }

  if (items.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Rows × Cols</TableHead>
              <TableHead className="text-right">Size</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">
                  <div className="truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.id}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {d.n_rows.toLocaleString()} × {d.n_cols}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtBytes(d.size_bytes)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {fmtDate(d.created_at)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${d.name}`}
                    onClick={() => setPendingDelete(d)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {list.data?.next_cursor ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={list.isFetching}
            onClick={() => setCursor(list.data?.next_cursor ?? undefined)}
          >
            {list.isFetching ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete dataset?</DialogTitle>
            <DialogDescription>
              "{pendingDelete?.name}" will be removed. Discoveries that already used it stay intact.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={del.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={del.isPending}>
              {del.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
